/**
 * Zeitraum-Filter der Event-Liste (Web und App).
 *
 * Der Filter besteht aus zwei ISO-Tagen: `from` („ab", URL-/API-Parameter `date`) und `to`
 * („bis", Parameter `dateTo`). Beide sind optional. „Die nächsten X Tage" ist kein eigener
 * Parameter, sondern nur eine Abkürzung: von heute bis heute + X — so bleibt ein geteilter Link
 * lesbar und das Backend kennt nur einen Zeitraum.
 */

/** Schnellwahl „nächste X Tage". */
export const NEXT_DAYS_PRESETS = [7, 30, 90] as const;

/** Lokales YYYY-MM-DD (kein UTC-Versatz wie bei toISOString — passt zum Backend-Filter). */
export function toIsoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** ISO-Tag plus `days` Tage (lokal gerechnet, Sommerzeit-fest). */
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return toIsoDay(new Date(y, m - 1, d + days));
}

/** Zeitraum „heute bis heute + days". */
export function nextDaysRange(days: number, today: Date = new Date()): { from: string; to: string } {
  const from = toIsoDay(today);
  return { from, to: addDaysIso(from, days) };
}

/**
 * „Dieses Wochenende“: der kommende Samstag und Sonntag. Am Samstag ab heute, am Sonntag nur
 * heute, denn der Samstag ist dann vorbei und die Liste zeigt ohnehin nur Kommendes.
 */
export function weekendRange(today: Date = new Date()): { from: string; to: string } {
  const from = toIsoDay(today);
  const wd = today.getDay();
  if (wd === 0) return { from, to: from };
  if (wd === 6) return { from, to: addDaysIso(from, 1) };
  const saturday = addDaysIso(from, 6 - wd);
  return { from: saturday, to: addDaysIso(saturday, 1) };
}

/** Ist genau „Dieses Wochenende“ gewählt? */
export function isWeekendRange(from: string | null | undefined, to: string | null | undefined, today: Date = new Date()): boolean {
  const r = weekendRange(today);
  return from === r.from && to === r.to;
}

/** Welche Schnellwahl entspricht genau diesem Zeitraum? `null`, wenn keine. */
export function matchingNextDaysPreset(from: string | null | undefined, to: string | null | undefined, today: Date = new Date()): number | null {
  if (!from || !to) return null;
  for (const days of NEXT_DAYS_PRESETS) {
    const r = nextDaysRange(days, today);
    if (r.from === from && r.to === to) return days;
  }
  return null;
}

/** Monatsnamen für die Schnellwahl „ganzer Monat“. */
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/**
 * Schnellwahl „ganzer Monat“ (28.10.2026, Vorbild Spotify): die nächsten `anzahl` Monate ab dem
 * laufenden. Der laufende Monat beginnt heute, nicht am Ersten — Vergangenes zeigt die Liste nicht.
 * `key` ist `YYYY-MM`.
 */
export function upcomingMonths(anzahl = 3, today: Date = new Date()): { key: string; label: string; from: string; to: string }[] {
  const out: { key: string; label: string; from: string; to: string }[] = [];
  for (let i = 0; i < anzahl; i++) {
    const erster = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const letzter = new Date(today.getFullYear(), today.getMonth() + i + 1, 0);
    out.push({
      key: toIsoDay(erster).slice(0, 7),
      label: MONATE[erster.getMonth()],
      from: i === 0 ? toIsoDay(today) : toIsoDay(erster),
      to: toIsoDay(letzter),
    });
  }
  return out;
}

/** Welcher Monat der Schnellwahl entspricht genau diesem Zeitraum? `null`, wenn keiner. */
export function matchingMonth(from: string | null | undefined, to: string | null | undefined, today: Date = new Date()): { key: string; label: string } | null {
  if (!from || !to) return null;
  const m = upcomingMonths(3, today).find(x => x.from === from && x.to === to);
  return m ? { key: m.key, label: m.label } : null;
}

/**
 * Beschriftung für den aktiven Filter-Chip. `fmt` formatiert einen ISO-Tag so, wie die jeweilige
 * Oberfläche Daten zeigt. `null`, wenn kein Zeitraum gesetzt ist.
 */
export function dateRangeLabel(
  from: string | null | undefined,
  to: string | null | undefined,
  fmt: (iso: string) => string,
  today: Date = new Date()
): string | null {
  const preset = matchingNextDaysPreset(from, to, today);
  if (preset != null) return `nächste ${preset} Tage`;
  if (isWeekendRange(from, to, today)) return 'dieses Wochenende';
  const monat = matchingMonth(from, to, today);
  if (monat) return monat.label;
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `ab ${fmt(from)}`;
  if (to) return `bis ${fmt(to)}`;
  return null;
}
